import React, { useEffect, useState } from 'react';
import Popup from 'reactjs-popup';
import { FaFilePdf, FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import { ModalPreparationsProps } from './ModalPreparations.types';
import { PreparationPDFFactory } from './PreparationPDFFactory';
import '../../styles/ModalPreparations.css';
import { useFormikContext, FormikValues } from 'formik';

const ModalPreparations: React.FC<ModalPreparationsProps> = ({
  prestacionName,
  specialtyId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pdfConfig, setPdfConfig] = useState<{ pdfUrl: string; title: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const { values, setFieldValue } = useFormikContext<FormikValues>();

  // Detectar si el usuario está en un dispositivo móvil
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      return mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
    };
    
    setIsMobile(checkIfMobile());
    
    // Re-verificar en caso de cambio de orientación
    const handleResize = () => {
      setIsMobile(checkIfMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Verificar si la especialidad requiere modal de preparaciones Y si hay una prestación válida
    const hasValidPrestacion = prestacionName && prestacionName.trim() !== '';
    const hasValidSpecialty = specialtyId && specialtyId > 0;
    
    // Verificar si es ecotomografía mamaria (basado en el nombre, no en specialtyId)
    const isEcotomografiaMamaria = prestacionName && prestacionName.toUpperCase().includes('ECOGRAFIA MAMARIA');
    
    const shouldOpen = (PreparationPDFFactory.requiresPreparationModal(specialtyId) || isEcotomografiaMamaria) && hasValidPrestacion && hasValidSpecialty;
    
    if (shouldOpen) {
      const config = PreparationPDFFactory.getPreparationPDF(prestacionName, specialtyId);
      const currentPreparacion = values.preparacion || '';
      const extra = `\n\nLa información completa de las preparaciones las puedes encontrar en el siguiente enlace: [Ver guía de preparación](${config?.pdfUrl})`;
      setPdfConfig(config);
      setError(null);
      setIsOpen(true);
      setFieldValue('preparacion', currentPreparacion + extra);
      
      if (!config) {
        setError('No se encontró información de preparación para esta prestación.');
      }
    } else {
      setIsOpen(false);
    }
  }, [prestacionName, specialtyId]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    // Scroll al inicio cuando se abre el modal
    setTimeout(() => {
      const modalContent = document.getElementsByClassName('preparation-modal-content');
      if (modalContent && modalContent[0]) {
        modalContent[0].scrollTop = 0;
      }
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <Popup
      modal
      lockScroll={false}
      closeOnDocumentClick={false}
      position="center center"
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
    >
      <div className="preparation-modal">
        <div className="preparation-modal-header">
          <div className="preparation-modal-title">
            <FaFilePdf size={24} color="white" />
            <div className="preparation-title-content">
              <h2>Guía de Preparación</h2>
              <p className="preparation-subtitle">{prestacionName}</p>
            </div>
          </div>
        </div>

        <div className="preparation-modal-content">
          {error ? (
            <div className="preparation-error">
              <p>{error}</p>
            </div>
          ) : pdfConfig ? (
            <>
              {isMobile ? (
                // Vista móvil: Botones para abrir/descargar el PDF
                <div className="mobile-pdf-container">
                  <div className="mobile-pdf-info">
                    <FaFilePdf size={64} color="#51B543" />
                    <h3>Guía de Preparación</h3>
                    <p className="mobile-pdf-description">
                      Para visualizar correctamente el documento de preparación, utiliza una de las siguientes opciones:
                    </p>
                  </div>
                  
                  <div className="mobile-pdf-actions">
                    <a
                      href={pdfConfig.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mobile-pdf-button open-button"
                    >
                      <FaExternalLinkAlt size={20} />
                      <span>Abrir en Nueva Pestaña</span>
                    </a>
                    
                    <a
                      href={pdfConfig.pdfUrl}
                      download={`Preparacion_${prestacionName}.pdf`}
                      className="mobile-pdf-button download-button"
                    >
                      <FaDownload size={20} />
                      <span>Descargar PDF</span>
                    </a>
                  </div>

                  <div className="mobile-pdf-note">
                    <p>
                      <strong>Nota:</strong> Si el documento no se descarga automáticamente, 
                      presiona "Abrir en Nueva Pestaña" y luego usa la opción de descarga de tu navegador.
                      Algunos navegadores móviles redirigen automáticamente a la opción de descarga.
                    </p>
                  </div>
                </div>
              ) : (
                // Vista escritorio: iframe tradicional
                <div className="pdf-viewer-container">
                  <iframe
                    src={pdfConfig.pdfUrl}
                    title={`Preparación para ${prestacionName}`}
                    className="pdf-viewer"
                    allow="fullscreen"
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                      setIsLoading(false);
                      setError('Error al cargar el documento PDF. Por favor, intente descargarlo directamente.');
                    }}
                  />
                  {isLoading && (
                    <div className="pdf-loading">
                      <div className="loading-spinner"></div>
                      <p>Cargando documento...</p>
                    </div>
                  )}
                </div>
              )}

              <div className="preparation-close-section">
                <div className="preparation-confirmation-message">
                  <p>Al cerrar admites haber leído o recibido las preparaciones necesarias</p>
                </div>
                <button
                  type="button"
                  className="close-button"
                  onClick={handleClose}
                >
                  Cerrar
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Popup>
  );
};

export default ModalPreparations;
